import React, { useEffect, useState, useReducer, useCallback, useContext } from "react";
import { Slide, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Box } from "@mui/material";
import DangerousIcon from "@mui/icons-material/Dangerous";

import { UserContext } from "../../context/UserContext";
import { getRequiredEPFields, getRequiredSDFields } from "../../components/Util/RequiredFields";
import { formatValue, getFileVersions, handleFileUpload, hasNonEmptyFiles, isDataChanged } from "../../util/FormUtils";
import { showErrorToast } from "../../util/showToast";
import BASE_URL from "../../config/apiConfig";
import ROLES from "../../util/roles";
import "./PlanStyles.css";
import "react-toastify/dist/ReactToastify.css";

// Components
import NotFound from "../error/NotFound";
import Header from "../../components/Header";
import SaveButtons from "../../components/XplanComponents/SaveButtons";
import PullListTable from "./PullListTable";
import Title from "../../components/XplanComponents/Title";
import TitleSMR from "../../components/XplanComponents/TitleSMR";
import Status from "../../components/XplanComponents/Status";
import StatusComment from "../../components/XplanComponents/StatusComment";
import CreatedInfo from "../../components/XplanComponents/CreatedInfo";
import Related from "../../components/XplanComponents/Related";
import SampleDisposal from "../../components/XplanComponents/SampleDisposal";
import ScopeOfWorkRequired from "../../components/XplanComponents/ScopeOfWorkRequired";
import CertificateOfDestructionRequired from "../../components/XplanComponents/CertificateOfDestructionRequired";
import MaterialTransferAgreementRequired from "../../components/XplanComponents/MaterialTransferAgreementRequired";
import DataTransferAgreementRequired from "../../components/XplanComponents/DataTransferAgreementRequired";
import ApprovalMessageRequired from "../../components/XplanComponents/ApprovalMessageRequired";
import ApprovalMessageComment from "../../components/XplanComponents/ApprovalMessageComment";
import Approval from "../../components/XplanComponents/Approval";
import ConfPub from "../../components/XplanComponents/ConfPub";
import NIHMSID from "../../components/XplanComponents/NIHMSID";
import Publications from "../../components/XplanComponents/Publications";
import ManagedBy from "../../components/XplanComponents/ManagedBy";
import InitialNotes from "../../components/XplanComponents/InitialNotes";
import Protocol from "../../components/XplanComponents/Protocol";
import OtherName from "../../components/XplanComponents/OtherName";
import Area from "../../components/XplanComponents/Area";
import RecipientInvestigator from "../../components/XplanComponents/RecipientInvestigator";
import Labs from "../../components/XplanComponents/Labs";
import LabsComment from "../../components/XplanComponents/LabsComment";
import ExperimentType from "../../components/XplanComponents/ExperimentType";
import Genetic from "../../components/XplanComponents/Genetic";
import AssayType from "../../components/XplanComponents/AssayType";
import AssayDetails from "../../components/XplanComponents/AssayDetails";
import MechPurpose from "../../components/XplanComponents/MechPurpose";
import ItnFunded from "../../components/XplanComponents/ItnFunded";
import ItnFundedMechanism from "../../components/XplanComponents/ItnFundedMechanism";
import ItnFundedComment from "../../components/XplanComponents/ItnFundedComment";
import ItnFundedShipping from "../../components/XplanComponents/ItnFundedShipping";
import ItnFundedShippingComment from "../../components/XplanComponents/ItnFundedShippingComment";
import SiteAgreementStatus from "../../components/XplanComponents/SiteAgreementStatus";
import ChairApproval from "../../components/XplanComponents/ChairApproval";
import ChairApprovalComment from "../../components/XplanComponents/ChairApprovalComment";
import ApprovedSOPComment from "../../components/XplanComponents/ApprovedSOPComment";
import ApprovedSOP from "../../components/XplanComponents/ApprovedSOP";
import RequestedCriteria from "../../components/XplanComponents/RequestedCriteria";
import SmrStatusComment from "../../components/XplanComponents/SmrStatusComment";
import BlindingRequired from "../../components/XplanComponents/BlindingRequired";
import BlindingComment from "../../components/XplanComponents/BlindingComment";
import RelabelingRequired from "../../components/XplanComponents/RelabelingRequired";
import RelabelingDetails from "../../components/XplanComponents/RelabelingDetails";
import DispositionOfSample from "../../components/XplanComponents/DispositionOfSample";
import GeneralComment from "../../components/XplanComponents/GeneralComment";
import LegacyPullList from "../../components/XplanComponents/LegacyPullList";
import DownloadUploadFields from "../../components/XplanComponents/DownloadUploadFields";
import BoxFileManager from "../../components/XplanComponents/BoxFileManager";
import BoxWorkflowFileManager from "../../components/XplanComponents/BoxWorkflowFileManager";
import HistoryLogTotal from "../../components/XplanComponents/HistoryLogTotal";
import UnloadPrompt from "../../components/Util/UnloadPrompt";

const dataReducer = (state, action) => {
    switch (action.type) {
        case "SET_DATA":
            return action.updatedData;
        default:
            return state;
    }
};

const EditXplan = () => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const newPlan = window.location.pathname === "/xplans/new";
    const defaultValues = {
        id: 0,
        epId: 0,
        versionId: 0,
        title: null,
        titleSMR: null,
        showInSMR: false,
        status: "Open",
        statusComment: null,
        related: "",
        assignedToGroup: null,
        priority: 3,
        sampleDisposal: "No",
        scopeOfWorkRequired: "Yes",
        scopeOfWork: "",
        scopeOfWorkVersion: 0,
        certificateOfDestructionRequired: null,
        sampleRequestForm: "",
        sampleRequestFormVersion: 0,
        materialTransferAgreementRequired: "Yes",
        materialTransferAgreement: "",
        materialTransferAgreementVersion: 0,
        dataTransferAgreementRequired: "No",
        dataTransferAgreement: "",
        dataTransferAgreementVersion: 0,
        approvalMessageRequired: null,
        approvalMessageComment: "",
        approvalMessage: "",
        approvalMessageVersion: 0,
        confPub: null,
        nihmsid: null,
        publications: null,
        createdBy: user.name,
        createdAt: null,
        updatedBy: null,
        updatedAt: null,
        managedBy: user.name,
        approvedBy: null,
        approvedDate: null,
        initialNotes: null,
        protocol: null,
        otherName: null,
        area: null,
        areaComment: null,
        recipientInvestigator: null,
        labs: null,
        labsComment: null,
        labsAddress: null,
        experimentType: null,
        genetic: null,
        assayType: null,
        assayDetails: null,
        mechPurpose: null,
        itnFunded: null,
        itnFundedMechanism: null,
        itnFundedComment: null,
        itnFundedShipping: null,
        itnFundedShippingComment: null,
        siteAgreementStatus: "N/A",
        chairApproval: null,
        chairApprovalComment: null,
        approvedSOP: null,
        approvedSOPComment: null,
        requestedCriteria: null,
        smrStatusComment: null,
        informedConsent: null,
        blindingRequired: null,
        blindingComment: null,
        relabelingRequired: null,
        relabelingDetails: null,
        dispositionOfSample: null,
        sortingComment: null,
        generalComment: null,
        approvalComment: null,
        boxFolderId: "",
        pullListFolderId: "",
        fileFolderId: "",
        approvalFolderId: "",
        legacyPullListLink: null,
        legacyMigratedPullList: false,
    };
    const defaultRequired = ["title", "status", "sampleDisposal"];

    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [epList, setEpList] = useState([]);
    const [xplans, setXplans] = useState([]);
    const [data, dispatchData] = useReducer(dataReducer, defaultValues);
    const [dataOriginal, setDataOriginal] = useState([]);
    const [required, setRequired] = useState(defaultRequired);
    const [isSaving, setIsSaving] = useState(false);
    const [title, setTitle] = useState();
    const [protocols, setProtocols] = useState([]);
    const [biologists, setBiologists] = useState([]);
    const [labs, setLabs] = useState({});
    const [assays, setAssays] = useState([]);
    const [couriers, setCouriers] = useState([]);
    const [scopeOfWorkFile, setScopeOfWorkFile] = useState();
    const [sampleRequestFormFile, setSampleRequestFormFile] = useState();
    const [materialTransferAgreementFile, setMaterialTransferAgreementFile] = useState();
    const [dataTransferAgreementFile, setDataTransferAgreementFile] = useState();
    const [scrollUp, setScrollUp] = useState(false);

    const fetchData = async () => {
        if (!newPlan) {
            let xplanList;
            try {
                const response = await axios.get(BASE_URL + "/api/xplans/find/" + window.location.pathname.split("/").pop(), { withCredentials: true });
                const newestData = { ...response.data[0] };
                newestData.createdAt = response.data[response.data.length - 1].createdAt;
                dispatchData({ type: "SET_DATA", updatedData: newestData });
                setDataOriginal({ ...newestData });
                setTitle(response.data[0].title);
                xplanList = response.data;
            } catch (error) {
                console.error("Error fetching data:", error);
                setNotFound(true);
                return;
            }
            setXplans(xplanList);
        }
        await axios.get(BASE_URL + "/api/xplans/findAllRecent", { withCredentials: true }).then((response) => {
            setEpList(response.data.sort((a, b) => b.epId - a.epId));
        });
        await axios.get(BASE_URL + "/api/studies", { withCredentials: true }).then((response) => {
            Object.values(response.data).forEach((study) => {
                protocols.push(study["Study"]);
            });
            setProtocols(protocols);
        });
        await axios.get(BASE_URL + "/api/biologists", { withCredentials: true }).then((response) => {
            let biologistsList = [];
            Object.values(response.data).forEach((biologist) => {
                biologistsList.push(biologist["Name"]);
            });
            setBiologists(biologistsList);
        });
        await axios.get(BASE_URL + "/api/couriers", { withCredentials: true }).then((response) => {
            let couriersList = [];
            Object.values(response.data).forEach((courier) => {
                couriersList.push(`${courier["Courier"]}: ${courier["Name"]} - Acct # ${courier["Account"]}`);
            });
            setCouriers(couriersList);
        });
        await axios.get(BASE_URL + "/api/labs", { withCredentials: true }).then((response) => {
            let labsList = {};
            Object.values(response.data).forEach((lab) => {
                labsList[lab.Code] = {
                    LabCode: lab.Code,
                    LabName: lab.Name,
                    LabDesc: lab.LabDesc,
                    ContactFirstName: lab.ContactFirstName,
                    ContactLastName: lab.ContactLastName,
                    ShippingAddress1: lab.ShippingAddress1,
                    ShippingAddress2: lab.ShippingAddress2,
                    ShippingAddress3: lab.ShippingAddress3,
                    ShippingAddress4: lab.ShippingAddress4,
                    ShippingCity: lab.ShippingCity,
                    ShippingState: lab.ShippingState,
                    ShippingPostalCode: lab.ShippingPostalCode,
                    PhoneNumber: lab.PhoneNumber,
                    EmailAddress: lab.EmailAddress,
                    PI: lab.PI,
                    ExPlan: lab.ExPlan,
                };
            });
            setLabs(labsList);
        });
        await axios.get(BASE_URL + "/api/assays", { withCredentials: true }).then((response) => {
            let assaysList = [];
            Object.values(response.data).forEach((assay) => {
                assaysList.push(assay["Assay"]);
            });
            setAssays(assaysList);
        });
    }

    useEffect(() => {
        fetchData().then(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (newPlan) {
            document.title = "New Plan";
        } else {
            if (title) {
                document.title = `EP${window.location.pathname.split("/").pop()}: ${title}`;
            } else {
                document.title = `EP${window.location.pathname.split("/").pop()}`;
            }
        }
    }, [title]);

    useEffect(() => {
        document.addEventListener("scroll", () => {
            if (window.pageYOffset > 300) {
                setScrollUp(true);
            } else {
                setScrollUp(false);
            }
        });
    }, []);

    useEffect(() => {
        const updateRequiredFields = () => {
            const requiredFields = new Set(defaultRequired);

            ["status", "approvalMessageRequired", "experimentType", "itnFunded", "chairApproval", "approvedSOP", "blindingRequired", "relabelingRequired"].forEach(field => {
                const fieldsRequired = data.sampleDisposal === "Yes" ? getRequiredSDFields(field, data) : getRequiredEPFields(field, data);
                fieldsRequired.forEach(subField => {
                    requiredFields.add(subField);
                });
            });

            if (data.status === "Pending Approval") {
                if (data.scopeOfWorkRequired === "Yes") {
                    requiredFields.add("scopeOfWork");
                } else {
                    requiredFields.add("sampleRequestForm");
                }
                if (data.materialTransferAgreementRequired === "Yes") {
                    requiredFields.add("materialTransferAgreement");
                }
                if (data.dataTransferAgreementRequired === "Yes") {
                    requiredFields.add("dataTransferAgreement");
                }
            }

            if (data.status === "Pending Approval" || data.status === "Real Time") {
                if (data.itnFunded === "Yes") {
                    requiredFields.add("itnFundedMechanism");
                } else {
                    requiredFields.add("itnFundedComment");
                    requiredFields.add("itnFundedShipping");
                }

                if (data.itnFundedShipping === "No") {
                    requiredFields.add("itnFundedShippingComment");
                }
            }

            setRequired(Array.from(requiredFields));
        };
        updateRequiredFields();
    }, [data.status, data.sampleDisposal, data.scopeOfWorkRequired, data.materialTransferAgreementRequired, data.dataTransferAgreementRequired, data.approvalMessageRequired, data.experimentType, data.itnFunded, data.chairApproval, data.approvedSOP, data.blindingRequired, data.relabelingRequired]);

    const handleChange = useCallback(
        (name, value) => {
            const newValue = formatValue(value);
            const newData = { ...data, [name]: newValue };
            dispatchData({ type: "SET_DATA", updatedData: newData });
        },
        [data, dispatchData]
    );

    const handleSampleDisposalChange = useCallback(
        (name, value) => {
            const newValue = formatValue(value);
            let newData;

            if (newValue === "Yes") {
                newData = {
                    ...data, [name]: newValue,
                    scopeOfWorkRequired: "No",
                    certificateOfDestructionRequired: "No",
                    materialTransferAgreementRequired: "No",
                    dataTransferAgreementRequired: "No",
                };
            } else {
                newData = { ...data, [name]: newValue };
            }

            dispatchData({ type: "SET_DATA", updatedData: newData });
        },
        [data, dispatchData]
    );

    const handleFileChange = (category, file) => {
        switch (category) {
            case "scopeOfWork":
                setScopeOfWorkFile(file);
                break;
            case "sampleRequestForm":
                setSampleRequestFormFile(file);
                break;
            case "materialTransferAgreement":
                setMaterialTransferAgreementFile(file);
                break;
            case "dataTransferAgreement":
                setDataTransferAgreementFile(file);
                break;
            default:
                break;
        }
    };

    const handleUploadAll = async (newData) => {
        const sowResponse = await handleFileUpload(
            newData.boxFolderId,
            "EP" + newData.epId + "_" + "ScopeOfWork" + ".",
            newData.scopeOfWork,
            scopeOfWorkFile
        );
        if (sowResponse) {
            newData.scopeOfWork = sowResponse.data.id;
            newData.scopeOfWorkVersion = await getFileVersions(newData.scopeOfWork);
        }

        const srfResponse = await handleFileUpload(
            newData.boxFolderId,
            "EP" + newData.epId + "_" + "SampleRequestForm" + ".",
            newData.sampleRequestForm,
            sampleRequestFormFile
        );
        if (srfResponse) {
            newData.sampleRequestForm = srfResponse.data.id;
            newData.sampleRequestFormVersion = await getFileVersions(newData.sampleRequestForm);
        }

        const mtaResponse = await handleFileUpload(
            newData.boxFolderId,
            "EP" + newData.epId + "_" + "MaterialTransferAgreement" + ".",
            newData.materialTransferAgreement,
            materialTransferAgreementFile
        );
        if (mtaResponse) {
            newData.materialTransferAgreement = mtaResponse.data.id;
            newData.materialTransferAgreementVersion = await getFileVersions(newData.materialTransferAgreement);
        }

        const dtaResponse = await handleFileUpload(
            newData.boxFolderId,
            "EP" + newData.epId + "_" + "DataTransferAgreement" + ".",
            newData.dataTransferAgreement,
            dataTransferAgreementFile
        );
        if (dtaResponse) {
            newData.dataTransferAgreement = dtaResponse.data.id;
            newData.dataTransferAgreementVersion = await getFileVersions(newData.dataTransferAgreement);
        }
    };

    const tagBoxFolders = async (newData) => {
        let protocolString = newData.protocol ? newData.protocol.replace(/\bN\/A\b/g, "").replace("Type 1 Diabetes", "T1D") : null;
        let protocolArray = protocolString ? newData.protocol.split(",").map(element => element.trim().split(" ")[0]).filter(element => element !== "") : [];
        let areaArray = newData.area ? newData.area.split(",") : [];

        await axios.put(BASE_URL + "/api/box/folder/tag", {
            folderId: newData.boxFolderId,
            tags: protocolArray.concat(areaArray),
        }, { withCredentials: true });
    }

    const loadData = async (newData) => {
        const response = await axios.get(BASE_URL + `/api/xplans/find/${newData.epId}`, { withCredentials: true });
        //const response = await axios.get(BASE_URL + `/api/xplans/versionId/${newData.epId}`);
        const versionCheck = response.data[0].versionId;

        if (versionCheck >= newData.versionId) {
            newData.versionId = 1 + versionCheck;
            /*
                Warn user they are saving over a newer version, ask if user wants to save/overwrite anyway

                setConflictData(newData);
                setShowConflictPrompt(true);
                else resumeLoad(newData);

                <ConflictPrompt
                    isOpen,
                    onOverwrite={() => resumeLoad(conflictData)
                    onCancel={handleCancel}
                />

                <AlertDialogTitle>New Version Conflict</AlertDialogTitle>
                <AlertDialogDescription>
                    A newer version was recently saved. Do you want to overwrite the existing version?
                </AlertDialogDescription>
                <AlertDialogAction onClick={onOverwrite}>Overwrite</AlertDialogAction>
                <AlertDialogCancel onClick={onCancel}>Back</AlertDialogCancel>
            */
        }

        await handleUploadAll(newData);
        axios.post(BASE_URL + `/api/xplans/update/${newData.epId}`, newData, { withCredentials: true }).then(async () => {
            if (newData.protocol !== dataOriginal.protocol || newData.area !== dataOriginal.area) {
                await tagBoxFolders(newData);
                window.location.reload();
            } else {
                window.location.reload();
            }
        });
    };

    const handleApproved = async (e) => {
        e.preventDefault();
        if (isSaving === false) {
            setIsSaving(true);
            let approvedData = {
                ...data,
                versionId: 1 + data.versionId,
                createdAt: null,
                updatedAt: new Date().toISOString(),
                updatedBy: user.name,
                status: "Plan Approved",
                approvedBy: user.name,
                approvedDate: new Date().toISOString().slice(0, 10),
            };
            await loadData(approvedData);
        }
    };

    const handleSubmit = async (e, forceSave = false) => {
        e.preventDefault();
        if (isSaving === false) {
            console.log("handleSubmit");
            setIsSaving(true);

            if (!forceSave) {
                // Check required fields
                let hasEmptyRequiredFields = false;
                const emptyRequiredFields = [];

                required.forEach((fieldName) => {
                    if ((!data[fieldName] && fieldName !== "scopeOfWork" && fieldName !== "sampleRequestForm" && fieldName !== "materialTransferAgreement" && fieldName !== "dataTransferAgreement" && fieldName !== "approvalMessage") ||
                        (fieldName === "scopeOfWork" && !data[fieldName] && !scopeOfWorkFile) ||
                        (fieldName === "sampleRequestForm" && !data[fieldName] && !sampleRequestFormFile) ||
                        (fieldName === "materialTransferAgreement" && !data[fieldName] && !materialTransferAgreementFile) ||
                        (fieldName === "dataTransferAgreement" && !data[fieldName] && !dataTransferAgreementFile)) {
                        hasEmptyRequiredFields = true;
                        emptyRequiredFields.push(fieldName);

                        const element = document.querySelector(`#${fieldName} :nth-child(2)`);
                        const uploadElement = document.querySelector(`#${fieldName} > div[style*="background-color: rgb(242, 243, 246);"]`);
                        if (element || uploadElement) {
                            element?.classList.add("requiredHighlight");
                            element.querySelector("div[role='button']")?.classList.add("requiredHighlight");
                            uploadElement?.classList.add("requiredHighlight");
                        }
                    }
                });

                if (hasEmptyRequiredFields) {
                    if (emptyRequiredFields.length > 0) {
                        const element = document.getElementById(emptyRequiredFields[0]);
                        if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                        }
                    }

                    //console.log(emptyRequiredFields);
                    showErrorToast("The highlighted required fields have not been filled out.", <DangerousIcon style={{ color: "#ef4337" }} />);
                    setIsSaving(false);
                    return false;
                }
            }

            const newData = { ...data };
            newData.updatedAt = new Date().toISOString();
            newData.versionId = 1 + data.versionId;
            newData.updatedBy = user.name;

            if (newPlan) {
                const countResponse = await axios.get(BASE_URL + "/api/xplans/count", { withCredentials: true });
                newData.epId = 1 + countResponse.data;

                const boxResponse = await axios.post(BASE_URL + "/api/box/folder/copy", {
                    sourceFolderId: "225758953818", // EP template: https://bri.app.box.com/folder/225758953818
                    destinationFolderId: "207729478272", // EP root directory: https://bri.app.box.com/folder/207729478272
                    folderName: "EP" + String(newData.epId).padStart(4, "0"),
                }, { withCredentials: true });
                newData.boxFolderId = boxResponse.data.id;

                const pullListFolderResponse = await axios.get(BASE_URL + `/api/box/folder/search/`, {
                    params: {
                        parentFolderId: newData.boxFolderId,
                        folderName: "Pull Lists",
                    },
                    withCredentials : true,
                });
                newData.pullListFolderId = pullListFolderResponse.data.id;

                const fileFolderResponse = await axios.get(BASE_URL + `/api/box/folder/search/`, {
                    params: {
                        parentFolderId: newData.boxFolderId,
                        folderName: "Files",
                    },
                    withCredentials : true,
                });
                newData.fileFolderId = fileFolderResponse.data.id;

                const approvalFolderResponse = await axios.get(BASE_URL + `/api/box/folder/search/`, {
                    params: {
                        parentFolderId: newData.boxFolderId,
                        folderName: "Approval Documents",
                    },
                    withCredentials : true,
                });
                newData.approvalFolderId = approvalFolderResponse.data.id;

                await handleUploadAll(newData);
                axios.post(BASE_URL + "/api/xplans/add", newData, { withCredentials: true }).then(async () => {
                    if (newData.protocol || newData.area) {
                        await tagBoxFolders(newData);
                        navigate(`/xplans/${newData.epId}`);
                        window.location.reload();
                    } else {
                        navigate(`/xplans/${newData.epId}`);
                        window.location.reload();
                    }
                });
            } else {
                newData.createdAt = null;
                if (newData.status === "Open" || newData.status === "Pending Approval" || newData.status === "Plan Withdrawn") {
                    newData.approvedBy = null;
                    newData.approvedDate = null;
                }
                if (isDataChanged(newData, dataOriginal) ||
                    hasNonEmptyFiles([scopeOfWorkFile, sampleRequestFormFile, materialTransferAgreementFile, dataTransferAgreementFile])) {
                    await loadData(newData);
                } else {
                    window.location.reload();
                }
            }
        }
    };

    // noinspection RequiredAttributes
    return (
        <>
            <Header/>
            {loading ? (
                <div>
                    <div className="loader center">
                        <i className="fa fa-cog fa-spin"/>
                    </div>
                </div>
            ) : notFound ? (
                <NotFound
                    id={window.location.pathname.split("/").pop()}
                />
            ) : (
                <div className="planSection">
                    <UnloadPrompt
                        currentData={data}
                        originalData={dataOriginal}
                        files={[
                            scopeOfWorkFile,
                            sampleRequestFormFile,
                            materialTransferAgreementFile,
                            dataTransferAgreementFile,
                        ]}
                        isSaving={isSaving}
                    />
                    <Box sx={{ pt: "1rem", pb: "4rem" }}>
                        {newPlan ? (
                            <h1 className="planTitle">New ITN Experimental Plan (EP)</h1>
                        ) : (
                            <h1 className="planTitle">
                                ITN Experimental Plan (EP) {data.epId}: {title}
                            </h1>
                        )}
                        <ul className="planSubtitleList">
                            <li>
                                <h2 className="planSubtitle">
                                    Fields marked with an asterisk{" "}
                                    <span style={{ color: "#cc0000" }}>*</span> are required.
                                </h2>
                            </li>
                            <li>
                                <h2 className="planSubtitle">
                                    Please remember to click SAVE at the bottom right after
                                    entering information.
                                </h2>
                            </li>
                        </ul>
                        <div className="planFields">
                            {!newPlan && !data.approvedBy && dataOriginal.status !== "Plan Withdrawn" &&
                                <Approval
                                    handleApproved={handleApproved}
                                    approvalComment={data.approvalComment}
                                    approvalFolderId={data.approvalFolderId}
                                    handleChange={handleChange}
                                    xplans={xplans}
                                />
                            }
                            <Title
                                title={data.title}
                                handleChange={handleChange}
                                xplans={xplans}
                            />
                            <TitleSMR
                                titleSMR={data.titleSMR}
                                showInSMR={data.showInSMR}
                                handleChange={handleChange}
                                xplans={xplans}
                            />
                            <Status
                                status={data.status}
                                handleChange={handleChange}
                                xplans={xplans}
                            />
                            {(data.status === "Withdrawn" || data.status === "Completed") && (
                                <StatusComment
                                    statusComment={data.statusComment}
                                    status={data.status}
                                    handleChange={handleChange}
                                    xplans={xplans}
                                    required={required}
                                />
                            )}
                            <CreatedInfo
                                createdBy={data.createdBy}
                                createdAt={data.createdAt}
                                approvedBy={data.approvedBy}
                                approvedDate={data.approvedDate}
                                updatedBy={data.updatedBy}
                                updatedAt={data.updatedAt}
                            />
                            <Related
                                related={data.related}
                                handleChange={handleChange}
                                xplans={xplans}
                                epList={epList}
                            />
                            <SampleDisposal
                                sampleDisposal={data.sampleDisposal}
                                handleSampleDisposalChange={handleSampleDisposalChange}
                                xplans={xplans}
                            />
                            <ScopeOfWorkRequired
                                scopeOfWorkRequired={data.scopeOfWorkRequired}
                                sampleDisposal={data.sampleDisposal}
                                handleChange={handleChange}
                                xplans={xplans}
                                required={required}
                            />
                            {data.scopeOfWorkRequired === "Yes" && (
                                <DownloadUploadFields
                                    category={"scopeOfWork"}
                                    data={data}
                                    handleFileChange={handleFileChange}
                                    xplans={xplans}
                                    allowedRoles={ROLES.ELEVATED}
                                    tag={ROLES.APPROVER}
                                    required={required}
                                />
                            )}
                            {data.scopeOfWorkRequired === "TS Sample Request" && (
                                <DownloadUploadFields
                                    category={"sampleRequestForm"}
                                    data={data}
                                    handleFileChange={handleFileChange}
                                    xplans={xplans}
                                    allowedRoles={ROLES.ELEVATED}
                                    tag={ROLES.APPROVER}
                                    required={required}
                                />
                            )}
                            {data.sampleDisposal === "Yes" && (
                                <CertificateOfDestructionRequired
                                    certificateOfDestructionRequired={data.certificateOfDestructionRequired}
                                    handleChange={handleChange}
                                    xplans={xplans}
                                    required={required}
                                />
                            )}
                            {data.sampleDisposal === "No" && (
                                <>
                                    <MaterialTransferAgreementRequired
                                        materialTransferAgreementRequired={data.materialTransferAgreementRequired}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    {data.materialTransferAgreementRequired === "Yes" && (
                                        <DownloadUploadFields
                                            category={"materialTransferAgreement"}
                                            data={data}
                                            handleFileChange={handleFileChange}
                                            xplans={xplans}
                                            allowedRoles={ROLES.ELEVATED}
                                            tag={ROLES.APPROVER}
                                            required={required}
                                        />
                                    )}
                                    <DataTransferAgreementRequired
                                        dataTransferAgreementRequired={data.dataTransferAgreementRequired}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    {data.dataTransferAgreementRequired === "Yes" && (
                                        <DownloadUploadFields
                                            category={"dataTransferAgreement"}
                                            data={data}
                                            handleFileChange={handleFileChange}
                                            xplans={xplans}
                                            allowedRoles={ROLES.ELEVATED}
                                            tag={ROLES.APPROVER}
                                            required={required}
                                        />
                                    )}
                                    <ApprovalMessageRequired
                                        approvalMessageRequired={data.approvalMessageRequired}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <ApprovalMessageComment
                                        approvalMessageComment={data.approvalMessageComment}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <ConfPub
                                        confPub={data.confPub}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                    />
                                    <NIHMSID
                                        nihmsid={data.nihmsid}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                    />
                                    {["Plan Approved", "Real Time", "Completed"].includes(data.status) &&
                                        <Publications
                                            publications={data.publications}
                                            handleChange={handleChange}
                                            xplans={xplans}
                                        />
                                    }
                                </>
                            )}
                            <ManagedBy
                                managedBy={data.managedBy}
                                handleChange={handleChange}
                                xplans={xplans}
                                biologists={biologists}
                                required={required}
                            />
                            <InitialNotes
                                initialNotes={data.initialNotes}
                                handleChange={handleChange}
                                xplans={xplans}
                                required={required}
                            />
                            <Protocol
                                protocol={data.protocol}
                                handleChange={handleChange}
                                xplans={xplans}
                                protocols={protocols}
                                required={required}
                            />
                            <OtherName
                                otherName={data.otherName}
                                handleChange={handleChange}
                                xplans={xplans}
                            />
                            <Area
                                area={data.area}
                                handleChange={handleChange}
                                xplans={xplans}
                                required={required}
                            />
                            {data.sampleDisposal === "No" && (
                                <>
                                    <RecipientInvestigator
                                        recipientInvestigator={data.recipientInvestigator}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <Labs
                                        labs={data.labs}
                                        labsAddress={data.labsAddress}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        labsDetails={labs}
                                        required={required}
                                    />
                                    <LabsComment
                                        labsComment={data.labsComment}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                    />
                                    <ExperimentType
                                        experimentType={data.experimentType}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    {data.experimentType === "Future Studies" && (
                                        <Genetic
                                            genetic={data.genetic}
                                            handleChange={handleChange}
                                            xplans={xplans}
                                            required={required}
                                        />
                                    )}
                                    <AssayType
                                        assayType={data.assayType}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        assays={assays}
                                        required={required}
                                    />
                                    <AssayDetails
                                        assayDetails={data.assayDetails}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <MechPurpose
                                        mechPurpose={data.mechPurpose}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <ItnFunded
                                        itnFunded={data.itnFunded}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    {data.itnFunded === "Yes" && (
                                        <ItnFundedMechanism
                                            itnFundedMechanism={data.itnFundedMechanism}
                                            handleChange={handleChange}
                                            xplans={xplans}
                                            required={required}
                                        />
                                    )}
                                    <ItnFundedComment
                                        itnFundedComment={data.itnFundedComment}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    {data.itnFunded === "No" && (
                                        <>
                                            <ItnFundedShipping
                                                itnFundedShipping={data.itnFundedShipping}
                                                handleChange={handleChange}
                                                xplans={xplans}
                                                required={required}
                                            />
                                            <ItnFundedShippingComment
                                                itnFundedShippingComment={data.itnFundedShippingComment}
                                                handleChange={handleChange}
                                                xplans={xplans}
                                                required={required}
                                            />
                                        </>
                                    )}
                                    <SiteAgreementStatus
                                        siteAgreementStatus={data.siteAgreementStatus}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                    />
                                </>
                            )}
                            <ChairApproval
                                chairApproval={data.chairApproval}
                                sampleDisposal={data.sampleDisposal}
                                handleChange={handleChange}
                                xplans={xplans}
                                required={required}
                            />
                            <ChairApprovalComment
                                chairApprovalComment={data.chairApprovalComment}
                                handleChange={handleChange}
                                xplans={xplans}
                                required={required}
                            />
                            {data.sampleDisposal === "No" && (
                                <>
                                    <ApprovedSOP
                                        approvedSOP={data.approvedSOP}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <ApprovedSOPComment
                                        approvedSOPComment={data.approvedSOPComment}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                </>
                            )}
                            <RequestedCriteria
                                requestedCriteria={data.requestedCriteria}
                                handleChange={handleChange}
                                xplans={xplans}
                                required={required}
                            />
                            {data.sampleDisposal === "No" && (
                                <>
                                    <BlindingRequired
                                        blindingRequired={data.blindingRequired}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    <BlindingComment
                                        blindingComment={data.blindingComment}
                                        handleChange={handleChange}
                                        xplans={xplans}
                                        required={required}
                                    />
                                    {data.blindingRequired === "Yes" &&
                                        <>
                                            <RelabelingRequired
                                                relabelingRequired={data.relabelingRequired}
                                                handleChange={handleChange}
                                                xplans={xplans}
                                                required={required}
                                            />
                                            {data.relabelingRequired === "Yes" && (
                                                <RelabelingDetails
                                                    relabelingDetails={data.relabelingDetails}
                                                    handleChange={handleChange}
                                                    xplans={xplans}
                                                    required={required}
                                                />
                                            )}
                                        </>
                                    }

                                </>
                            )}
                            {(data.status === "Real Time" || data.dispositionOfSample) && (
                                <DispositionOfSample
                                    dispositionOfSample={data.dispositionOfSample}
                                    handleChange={handleChange}
                                    xplans={xplans}
                                    required={required}
                                />
                            )}
                            <SmrStatusComment
                                smrStatusComment={data.smrStatusComment}
                                handleChange={handleChange}
                                xplans={xplans}
                            />
                            <GeneralComment
                                generalComment={data.generalComment}
                                handleChange={handleChange}
                                xplans={xplans}
                            />

                            <div id="workflows-div" className="xplanLine"/>
                            {!newPlan &&
                                <PullListTable
                                    epId={data.epId}
                                    pullListFolderId={data.pullListFolderId}
                                    sampleDisposal={data.sampleDisposal}
                                    lab={labs[data.labs]}
                                    labLegacyAddress={labs[data.labs] ? undefined : data.labsAddress}
                                    couriers={couriers}
                                    isEPApproved={!!data.approvedBy}
                                />
                            }

                            {(data.legacyPullListLink || data.legacyMigratedPullList) && <div className="xplanLine"/>}
                            {(data.legacyPullListLink || data.legacyMigratedPullList) &&
                                <LegacyPullList
                                    legacyPullListLink={data.legacyPullListLink}
                                    legacyMigratedPullList={data.legacyMigratedPullList}
                                    pullListFolderId={data.pullListFolderId}
                                />
                            }

                            {!newPlan && <div id="box-files-div" className="xplanLine"/>}
                            {!newPlan &&
                                <>
                                    <BoxFileManager
                                        folderId={data.fileFolderId}
                                        title={`EP${data.epId} Files`}
                                        allowedRoles={ROLES.ELEVATED}
                                    />
                                    <BoxWorkflowFileManager
                                        epId={data.epId}
                                        pullListFolderId={data.pullListFolderId}
                                    />
                                </>
                            }
                        </div>
                        {!newPlan && <div className="xplanLine"/>}
                        {!newPlan && <HistoryLogTotal xplans={xplans}/>}
                    </Box>
                    <SaveButtons
                        scrollUp={scrollUp}
                        handleSubmit={handleSubmit}
                        handleApproved={handleApproved}
                        showApprovalButton={!newPlan && !data.approvedBy && dataOriginal.status !== "Plan Withdrawn"}
                        isSaving={isSaving}
                    />
                    <ToastContainer
                        position="top-center"
                        autoClose={3000}
                        hideProgressBar
                        newestOnTop
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss={false}
                        draggable
                        pauseOnHover
                        theme="light"
                        transition={Slide}
                    />
                </div>
            )}
        </>
    );
}

export default EditXplan;